// VendAgain — create / edit listing form. Reuses ImageUploader for photos.
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, X, DollarSign } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import ImageUploader from "@/components/ImageUploader";
import { CATEGORIES, SECTIONS, CONDITIONS, formatPrice } from "./shared";

const EMPTY = {
  category: "",
  title: "",
  description: "",
  city: "",
  condition: "good",
  price_cents: 0,
  best_offer: false,
  image_urls: [],
};

export default function VendAgainNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [form, setForm] = useState(EMPTY);
  const [priceInput, setPriceInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!editing);

  useEffect(() => {
    if (!editing) return;
    let alive = true;
    api.get(`/vendagain/listings/${id}`).then(({ data }) => {
      if (!alive) return;
      if (!data.is_owner) {
        toast.error("You can only edit your own listings.");
        navigate("/vendagain/mine");
        return;
      }
      setForm({
        category: data.category,
        title: data.title,
        description: data.description,
        city: data.city,
        condition: data.condition,
        price_cents: data.price_cents,
        best_offer: data.best_offer,
        image_urls: data.image_urls || [],
      });
      setPriceInput(data.price_cents ? (data.price_cents / 100).toString() : "");
      setLoaded(true);
    }).catch(() => {
      toast.error("Listing not found");
      navigate("/vendagain/mine");
    });
    return () => { alive = false; };
  }, [id, editing, navigate]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPriceChange = (raw) => {
    setPriceInput(raw);
    const n = parseFloat(raw);
    patch("price_cents", isNaN(n) || n < 0 ? 0 : Math.round(n * 100));
  };

  const submit = async () => {
    if (!form.category) return toast.error("Pick a category");
    if (form.title.trim().length < 3) return toast.error("Title too short");
    if (form.description.trim().length < 10) return toast.error("Description too short (10+ chars)");
    if (form.city.trim().length < 2) return toast.error("Add a city");
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/vendagain/listings/${id}`, form);
        toast.success("Listing updated");
      } else {
        const { data } = await api.post("/vendagain/listings", form);
        toast.success("Listed! Buyers can now reach you.");
        navigate(`/vendagain/listings/${data.id}`);
        return;
      }
      navigate("/vendagain/mine");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-14 text-center">
        <h1 className="font-serif text-3xl">Sign in to post an item</h1>
        <Link to="/register" className="btn-primary rounded-full inline-flex mt-6 px-6 py-3 font-bold">
          Get started free
        </Link>
      </div>
    );
  }

  if (!loaded) {
    return <div className="mx-auto max-w-3xl px-6 py-14 opacity-60">Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-14" data-testid="vendagain-form-page">
      <Link to="/vendagain" className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100">
        <ArrowLeft size={14} /> Back to VendAgain
      </Link>
      <h1 className="font-serif text-4xl mt-4">
        {editing ? "Edit your listing" : "Post something to VendAgain"}
      </h1>
      <p className="mt-2 opacity-70">
        Don&rsquo;t need it? Vend it. Give it a clear title, a fair price, and one great photo.
      </p>

      <div className="mt-10 space-y-8">
        {/* CATEGORY */}
        <div>
          <div className="overline mb-2">Category</div>
          {SECTIONS.map((s) => (
            <div key={s.key} className="mb-4">
              <div className="text-xs uppercase tracking-widest opacity-60 mb-2">{s.label}</div>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.filter((c) => c.section === s.key).map((c) => {
                  const active = form.category === c.value;
                  const Icon = c.icon;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => patch("category", c.value)}
                      data-testid={`vendagain-form-cat-${c.value}`}
                      className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition ${active ? "ring-2 ring-offset-1" : ""}`}
                      style={{ background: c.color, color: c.ink }}
                    >
                      <Icon size={12} /> {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* TITLE + DESC */}
        <div>
          <div className="overline mb-2">Title</div>
          <Input
            value={form.title}
            onChange={(e) => patch("title", e.target.value)}
            placeholder="e.g. 3-basket commercial fryer, works great"
            maxLength={140}
            data-testid="vendagain-form-title"
          />
        </div>
        <div>
          <div className="overline mb-2">Description</div>
          <Textarea
            rows={5}
            value={form.description}
            onChange={(e) => patch("description", e.target.value)}
            placeholder="Age, condition details, dimensions, why you're selling, pickup or delivery, anything a buyer would want to know."
            maxLength={2000}
            data-testid="vendagain-form-desc"
          />
          <div className="text-[11px] text-right opacity-50 mt-1">{form.description.length}/2000</div>
        </div>

        {/* PRICE + BEST OFFER + CONDITION */}
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <div className="overline mb-2">Price</div>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
              <Input
                type="number"
                min="0"
                step="0.01"
                value={priceInput}
                onChange={(e) => onPriceChange(e.target.value)}
                placeholder="0 for Free"
                disabled={form.best_offer}
                className="pl-9"
                data-testid="vendagain-form-price"
              />
            </div>
            <label className="mt-2 inline-flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={form.best_offer}
                onChange={(e) => patch("best_offer", e.target.checked)}
                data-testid="vendagain-form-best-offer"
                className="h-4 w-4 accent-[color:var(--avt-purple)]"
              />
              Best offer — invite buyers to name a price
            </label>
            <div className="mt-2 text-xs opacity-60">Preview: {formatPrice(form.price_cents, form.best_offer)}</div>
          </div>
          <div>
            <div className="overline mb-2">Condition</div>
            <div className="flex flex-wrap gap-2">
              {CONDITIONS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => patch("condition", c.value)}
                  data-testid={`vendagain-form-cond-${c.value}`}
                  className={`text-xs px-3 py-1 rounded-full transition ${form.condition === c.value ? "" : "opacity-60"}`}
                  style={{
                    background: form.condition === c.value ? "var(--avt-purple)" : "rgba(0,0,0,0.06)",
                    color: form.condition === c.value ? "#fff" : "var(--ink-soft)",
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* CITY */}
        <div>
          <div className="overline mb-2">City</div>
          <Input
            value={form.city}
            onChange={(e) => patch("city", e.target.value)}
            placeholder="Where's the item located?"
            maxLength={80}
            data-testid="vendagain-form-city"
          />
        </div>

        {/* PHOTOS */}
        <div>
          <div className="overline mb-2 inline-flex items-center gap-2">
            <ImagePlus size={14} /> Photos (up to 6)
          </div>
          {form.image_urls.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
              {form.image_urls.map((url, idx) => (
                <div
                  key={idx}
                  className="relative aspect-square rounded-sm overflow-hidden"
                  data-testid={`vendagain-form-image-${idx}`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => patch("image_urls", form.image_urls.filter((_, i) => i !== idx))}
                    data-testid={`vendagain-form-image-remove-${idx}`}
                    className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1"
                    aria-label="Remove photo"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {form.image_urls.length < 6 && (
            <ImageUploader
              onUploaded={(url) => patch("image_urls", [...form.image_urls, url])}
              testId="vendagain-form-image-upload"
              label="Add a photo"
            />
          )}
        </div>

        <div className="flex gap-3">
          <Button
            onClick={submit}
            disabled={busy}
            className="btn-primary rounded-full inline-flex items-center gap-2 flex-1"
            data-testid="vendagain-form-submit"
          >
            {editing ? "Save changes" : "Publish listing"}
          </Button>
          <Link to="/vendagain" className="inline-flex">
            <Button variant="outline" className="rounded-full">Cancel</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
